'use client';
import type { Assign, PolymorphicProps } from '@ark-ui/react';
import { Dialog } from '@ark-ui/react';
import { ark } from '@ark-ui/react';
import { type DrawerVariantProps, drawer } from '@terra/ui-styled-system';
import type { ComponentProps, HTMLStyledProps } from '@terra/ui-styled-system';
import { createStyleContext } from './utils/create-style-context';

const { withRootProvider, withContext } = createStyleContext(drawer);

export type RootProviderProps = ComponentProps<typeof RootProvider>;
export const RootProvider = withRootProvider<
  Assign<Dialog.RootProviderProps, DrawerVariantProps>
>(Dialog.RootProvider);

const StyledRoot = withRootProvider<
  Assign<Dialog.RootProps, DrawerVariantProps>
>(Dialog.Root);

export type RootProps = ComponentProps<typeof StyledRoot>;

const OUTSIDE_INTERACTION_SAFE_SELECTOR = '[data-outside-interaction-safe]';

const isOutsideInteractionSafe = (event: { target: EventTarget | null }) =>
  event.target instanceof Element &&
  Boolean(event.target.closest(OUTSIDE_INTERACTION_SAFE_SELECTOR));

export const Root = ({
  onInteractOutside,
  onPointerDownOutside,
  onFocusOutside,
  ...props
}: RootProps) => (
  <StyledRoot
    modal={false}
    trapFocus={false}
    preventScroll={false}
    onInteractOutside={(event) => {
      if (isOutsideInteractionSafe(event.detail.originalEvent)) {
        event.preventDefault();
        return;
      }
      onInteractOutside?.(event);
    }}
    onPointerDownOutside={(event) => {
      if (isOutsideInteractionSafe(event.detail.originalEvent)) {
        event.preventDefault();
        return;
      }
      onPointerDownOutside?.(event);
    }}
    onFocusOutside={(event) => {
      if (isOutsideInteractionSafe(event.detail.originalEvent)) {
        event.preventDefault();
        return;
      }
      onFocusOutside?.(event);
    }}
    {...props}
  />
);

export const Backdrop = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Dialog.BackdropProps>
>(Dialog.Backdrop, 'backdrop');

export const CloseTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, Dialog.CloseTriggerProps>
>(Dialog.CloseTrigger, 'closeTrigger');

export const Content = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Dialog.ContentProps>
>(Dialog.Content, 'content');

export const Description = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Dialog.DescriptionProps>
>(Dialog.Description, 'description');

export const Positioner = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Dialog.PositionerProps>
>(Dialog.Positioner, 'positioner');

export const Title = withContext<
  HTMLHeadingElement,
  Assign<HTMLStyledProps<'h2'>, Dialog.TitleProps>
>(Dialog.Title, 'title');

export const Trigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, Dialog.TriggerProps>
>(Dialog.Trigger, 'trigger');

export const Header = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, PolymorphicProps>
>(ark.div, 'header');

export const Body = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, PolymorphicProps>
>(ark.div, 'body');

export const Footer = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, PolymorphicProps>
>(ark.div, 'footer');

export {
  DialogContext as Context,
  type DialogContextProps as ContextProps,
} from '@ark-ui/react';
